/* bound-bundle-builder.js · Bound M3 (custom)
 * Pack PDP builder per reference/pdp.png: N per-pair <select>s ("<Colour> / <Size>"), pre-filled
 * with the bestseller mix in the default size (?size= overrides), always-submittable CTA.
 * Pack size is fixed per page (switching packs = navigating to a sibling product URL).
 *
 * Cart integration (Strategy A): the Simple Bundles "Infinite Options" app embed injects its own
 * per-pair <select>s into the product <form>. Our visible pair selects mirror onto them 1:1
 * (set value + dispatch 'change') so the app builds properties[_bundle_selection]; the CTA submits
 * the native form. syncBundleSelection + mirrorStock are FINALISED against the live app embed
 * (verified 2026-07-23 on the draft theme with the pack Active) — see the block comment below.
 */
(function () {
  'use strict';

  // Bestseller-mix counts, positional over the colour list [c0, c1, c2] = Black, Navy, Green.
  var BESTSELLER = { 1: [1, 0, 0], 3: [2, 1, 0], 5: [3, 1, 1], 7: [5, 1, 1] };

  function firstWord(s) { return (s || '').trim().split(/\s+/)[0]; }
  function norm(s) { return (s || '').toString().toLowerCase().replace(/\s+/g, ' ').trim(); }

  function initBuilder(root) {
    var host = root.closest('[data-section]');
    var sectionId = host ? host.getAttribute('data-section') : null;
    var cfgEl = sectionId ? document.querySelector('[data-bound-bb-config="' + sectionId + '"]') : null;
    if (!cfgEl) { return; }

    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }

    var pairs = cfg.pairs;
    var colours = cfg.colours || [];
    var sizes = cfg.sizes || [];
    var swatches = cfg.swatches || {};

    var els = {
      summary: root.querySelector('[data-bb-summary]'),
      form: root.querySelector('form'),
      tiles: host ? host.querySelectorAll('.bound-tile[href]') : [],
      pairSelects: root.querySelectorAll('[data-bb-pair]')
    };

    function pairValue(sel) {
      // "Black Ink / M" -> { colour: "Black Ink", size: "M" }
      var parts = (sel.value || '').split(' / ');
      return { colour: (parts[0] || '').trim(), size: (parts[1] || '').trim() };
    }

    function setPair(sel, colour, size) {
      var v = colour + ' / ' + size;
      if (sel.value !== v) { sel.value = v; }
      paintSwatch(sel);
    }

    function paintSwatch(sel) {
      var field = sel.closest('.bound-bb__pairfield');
      var dot = field ? field.querySelector('[data-bb-pairswatch]') : null;
      if (dot) { dot.style.background = swatches[pairValue(sel).colour] || '#ccc'; }
    }

    // Majority size across pairs — carried on the pack-tile links as ?size=.
    function majoritySize() {
      var counts = {};
      Array.prototype.forEach.call(els.pairSelects, function (sel) {
        var s = pairValue(sel).size;
        counts[s] = (counts[s] || 0) + 1;
      });
      var best = null;
      Object.keys(counts).forEach(function (s) {
        if (!best || counts[s] > counts[best]) { best = s; }
      });
      return best;
    }

    function updateTileHrefs() {
      var sv = majoritySize();
      Array.prototype.forEach.call(els.tiles, function (a) {
        var base = a.getAttribute('data-pack-url') || a.getAttribute('href').split('?')[0];
        a.setAttribute('href', sv ? base + '?size=' + encodeURIComponent(sv) : base);
      });
    }

    function render() {
      var colourCounts = {};
      Array.prototype.forEach.call(els.pairSelects, function (sel) {
        var pv = pairValue(sel);
        colourCounts[pv.colour] = (colourCounts[pv.colour] || 0) + 1;
        paintSwatch(sel);
      });

      // "Your pack: 3 Black · 1 Navy · 1 Green" (sizes are visible on each pair row).
      if (els.summary) {
        var parts = [];
        colours.forEach(function (c) {
          if (colourCounts[c]) { parts.push(colourCounts[c] + ' ' + firstWord(c)); }
        });
        els.summary.textContent = '';
        if (parts.length) {
          els.summary.appendChild(document.createTextNode((cfg.summaryPrefix || 'Your pack:') + ' '));
          var b = document.createElement('b');
          b.textContent = parts.join(' · ');
          els.summary.appendChild(b);
        }
      }

      updateTileHrefs();
      syncBundleSelection();
      refreshAvailability();
    }

    /* ============================================================
     * Simple Bundles integration — FINALISED against the live app embed
     * (simple-bundles-v2 / "Infinite options"), verified 2026-07-23 on the draft theme
     * with the pack temporarily Active. Confirmed behaviour:
     *   - The app injects <div id="simple-bundles-io-options"> INSIDE our product <form>,
     *     holding one <select name="properties[Pair N]"> per pair (ids pair-1..pair-N) plus
     *     <select name="properties[Gift Box]"> (which the app auto-fills — we never touch it).
     *   - Each Pair option value is "<Master title> - <Colour> / <Size>"
     *     (e.g. "The Bound Boxer - Black Ink / M"); sold-out combos are <option disabled>
     *     (disable_oos_options:true).
     *   - On 'change', the app rebuilds <input name="properties[_bundle_selection]"> as
     *     "pair1 <> pair2 <> ... <> pairN <> Gift Box" (values joined by " <> ").
     * Strategy A: we set each Pair <select> to the option matching our visible pair choice
     * and dispatch 'change' — the app owns _bundle_selection (zero payload-format risk).
     * The app renders its selects VISIBLY, so we hide its container (hideAppOptions + the
     * CSS rule on #simple-bundles-io-options).
     * ============================================================ */

    // The app's per-pair selects (properties[Pair 1..N]) inside our form — excludes our
    // visible selects (data-bb-ignore) and the app's Gift Box select.
    function bundlePairSelects() {
      if (!els.form) { return []; }
      return Array.prototype.filter.call(els.form.querySelectorAll('select'), function (s) {
        return /^properties\[Pair /.test(s.getAttribute('name') || '');
      });
    }

    // Match the app <option> for a colour+size. App option values END WITH "<Colour> / <Size>"
    // (prefixed by the master product title), so match on that suffix — prefix-agnostic.
    function matchOption(select, colour, sz) {
      var suffix = norm(colour + ' / ' + sz);
      var found = null;
      Array.prototype.forEach.call(select.options, function (opt) {
        if (found) { return; }
        var v = norm(opt.value);
        if (v === suffix || v.slice(-suffix.length) === suffix) { found = opt; }
      });
      return found;
    }

    // Push each visible pair choice onto the app's matching Pair select (Strategy A).
    // Skips sold-out (disabled) options — the browser won't select them anyway.
    // Re-dispatches 'change' whenever the app's select, its hidden properties[Pair N] input or
    // its _bundle_selection entry disagrees with our pick: the app's late init re-renders its
    // selects and resets those inputs to its own defaults (Black Ink / XS), see initBuilder.
    function syncBundleSelection() {
      var selects = bundlePairSelects();
      if (!selects.length) { return; }              // app not injected (yet) — inert
      var bsInput = els.form.querySelector('input[name="properties[_bundle_selection]"]');
      var bsParts = bsInput ? (bsInput.value || '').split(' <> ') : null;
      Array.prototype.forEach.call(els.pairSelects, function (sel, idx) {
        var target = selects[idx];
        if (!target) { return; }
        var pv = pairValue(sel);
        var opt = matchOption(target, pv.colour, pv.size);
        if (!opt || opt.disabled) { return; }
        var pairInput = els.form.querySelector('input[name="' + target.getAttribute('name') + '"]');
        if (target.value !== opt.value ||
            (pairInput && pairInput.value !== opt.value) ||
            (bsParts && bsParts[idx] !== opt.value)) {
          target.value = opt.value;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Mirror the app's out-of-stock state (disable_oos_options:true) onto our visible selects:
    // sold-out colour/size combos become <option disabled> (greyed in the dropdown list).
    // Runs once, when the app's selects appear. refreshAvailability() then reflects it.
    function mirrorStock() {
      var appSelects = bundlePairSelects();
      if (!appSelects.length) { return; }
      Array.prototype.forEach.call(els.pairSelects, function (sel, idx) {
        var src = appSelects[idx] || appSelects[0];
        Array.prototype.forEach.call(sel.options, function (opt) {
          var parts = (opt.value || '').split(' / ');
          var appOpt = matchOption(src, parts[0], parts[1]);
          opt.disabled = appOpt ? appOpt.disabled : false;
        });
      });
      refreshAvailability();
    }

    // Reflect stock in the UI (called on every render + after mirrorStock): grey a pair row
    // whose current pick is sold out, and update the CTA (fully sold out → disabled "Sold out";
    // any pair still on a sold-out pick → disabled; otherwise enabled with the original label).
    function refreshAvailability() {
      var anyAvailable = false, anyPairOOS = false;
      Array.prototype.forEach.call(els.pairSelects, function (sel) {
        Array.prototype.forEach.call(sel.options, function (opt) { if (!opt.disabled) { anyAvailable = true; } });
        var selOpt = sel.options[sel.selectedIndex];
        var curOOS = selOpt ? !!selOpt.disabled : true;
        if (curOOS) { anyPairOOS = true; }
        var row = sel.closest('.bound-bb__pair');
        if (row) { row.classList.toggle('is-oos', curOOS); }
      });
      reflectAvailability(!anyAvailable, anyPairOOS);
    }

    function reflectAvailability(soldOut, anyPairOOS) {
      var cta = root.querySelector('[data-bb-cta]');
      if (!cta) { return; }
      var label = cta.querySelector('.bound-bb__cta-text');
      if (label && cta.getAttribute('data-cta-label') === null) {
        cta.setAttribute('data-cta-label', label.textContent);
      }
      var original = cta.getAttribute('data-cta-label');
      if (soldOut) {
        cta.disabled = true;
        if (label) { label.textContent = cfg.soldOutLabel || 'Sold out'; }
      } else {
        cta.disabled = anyPairOOS;
        if (label && original !== null) { label.textContent = original; }
      }
      if (typeof syncSticky === 'function') { syncSticky(); }
    }

    // Simple Bundles renders its selects visibly in #simple-bundles-io-options; hide that block
    // (kept in the DOM so it still posts _bundle_selection). CSS also hides the id for a no-flash
    // result; this JS fallback covers the case where the app's container id ever changes.
    function hideAppOptions() {
      var box = document.getElementById('simple-bundles-io-options');
      if (!box) {
        var selects = bundlePairSelects();
        if (!selects.length) { return; }
        box = selects[0];
        while (box && !selects.every(function (s) { return box.contains(s); })) { box = box.parentElement; }
      }
      if (box) { box.classList.add('bound-bb__sbhidden'); }
    }

    // ---- events ----
    Array.prototype.forEach.call(els.pairSelects, function (sel) {
      sel.addEventListener('change', render);
    });

    // "Size guide →" opens the Sizing accordion below and scrolls to it (the link's href is the
    // no-JS fallback). Matches the accordion whose heading mentions "siz".
    var sizeGuide = root.querySelector('[data-bb-sizeguide]');
    if (sizeGuide) {
      sizeGuide.addEventListener('click', function (e) {
        var target = null;
        Array.prototype.forEach.call(root.querySelectorAll('.bound-bb__acc details'), function (d) {
          var sum = d.querySelector('summary');
          if (!target && sum && /siz/i.test(sum.textContent)) { target = d; }
        });
        if (!target) { return; }
        e.preventDefault();
        target.open = true;
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        var sum = target.querySelector('summary');
        if (sum) { sum.focus({ preventScroll: true }); }
      });
    }

    // ---- sticky add-to-cart (mobile) ----
    // The sticky button CLICKS the in-card CTA, so the submit runs through exactly the same path
    // (native product form → Simple Bundles' properties → Liquid Ajax Cart). It mirrors the CTA's
    // disabled state and its "Sold out" label; otherwise it keeps its own label.
    var sticky = host ? host.querySelector('[data-bb-sticky]') : null;
    var stickyBtn = sticky ? sticky.querySelector('[data-bb-sticky-cta]') : null;
    var ctaBtn = root.querySelector('[data-bb-cta]');
    function syncSticky() {
      if (!stickyBtn || !ctaBtn) { return; }
      stickyBtn.disabled = ctaBtn.disabled;
      var ctaLabel = ctaBtn.querySelector('.bound-bb__cta-text');
      var soldOut = ctaLabel && ctaLabel.textContent.trim() === (cfg.soldOutLabel || 'Sold out');
      stickyBtn.textContent = soldOut ? ctaLabel.textContent.trim() : stickyBtn.getAttribute('data-label');
    }
    if (stickyBtn && ctaBtn) {
      stickyBtn.addEventListener('click', function () {
        if (ctaBtn.disabled) { return; }
        syncBundleSelection();
        ctaBtn.click();
      });
      new MutationObserver(syncSticky).observe(ctaBtn, { attributes: true, childList: true, characterData: true, subtree: true });
      // The in-card error message is off-screen on mobile — bring it into view when it appears.
      var errWrap = root.querySelector('.product-form__error-message-wrapper');
      if (errWrap) {
        new MutationObserver(function () {
          if (!errWrap.hidden && getComputedStyle(sticky).display !== 'none') {
            errWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }).observe(errWrap, { attributes: true, attributeFilter: ['hidden'] });
      }
      // Expose the bar height so the page can pad its bottom and lift the chat bubble above it.
      var setH = function () { document.documentElement.style.setProperty('--bound-sticky-h', sticky.offsetHeight + 'px'); };
      if ('ResizeObserver' in window) { new ResizeObserver(setH).observe(sticky); }
      setH();
    }

    // ---- init: pre-fill bestseller mix in the default (or ?size=) size ----
    var params = new URLSearchParams(window.location.search);
    var urlSize = params.get('size');
    var startSize = (urlSize && sizes.indexOf(urlSize) !== -1) ? urlSize : (cfg.defaultSize || sizes[0]);

    var mix = BESTSELLER[pairs] || BESTSELLER[5];
    var fillList = [];
    colours.forEach(function (c, idx) {
      for (var n = 0; n < (mix[idx] || 0); n++) { fillList.push(c); }
    });
    Array.prototype.forEach.call(els.pairSelects, function (sel, idx) {
      var colour = fillList[idx] || colours[0];
      setPair(sel, colour, startSize);
    });
    render();

    // Keep the app in sync with the pre-fill. Read from simple-bundles-v2.min.js (8 Oct 2026): the
    // app (re)renders its selects into [data-simple-bundles-options] from its stored HTML — on its
    // defaults (first option, Black Ink / XS) — and resets the hidden properties[Pair N] /
    // _bundle_selection inputs: on DOMContentLoaded, again 500ms later, whenever an ATTRIBUTE of
    // the form's input[name="id"] changes, and on a back/forward-cache restore (pageshow). Each
    // re-render replaces nodes inside the form, so a debounced childList observer (never
    // disconnected) re-syncs after every one; pageshow is handled explicitly as well. Every step
    // is idempotent: syncBundleSelection only fires 'change' on a mismatch.
    // NOTE: never change attributes on this form's input[name="id"] — that makes the app re-render.
    if (els.form) {
      var resync = function () {
        if (!bundlePairSelects().length) { return; }
        hideAppOptions(); mirrorStock(); syncBundleSelection();
      };
      var resyncTimer = 0;
      new MutationObserver(function () {
        clearTimeout(resyncTimer);
        resyncTimer = setTimeout(resync, 50);
      }).observe(els.form, { childList: true, subtree: true });
      resync();
      window.addEventListener('pageshow', function (e) { if (e.persisted) { resync(); } });

      // Right before every add-to-cart: re-sync, then verify what the form will actually post and
      // block the add if it still disagrees with the visible picks (guardSubmit). Capture phase on
      // the form runs before Liquid Ajax Cart's own submit listener reads the FormData; the app
      // rebuilds its inputs synchronously on 'change'.
      els.form.addEventListener('submit', guardSubmit, true);
      if (ctaBtn) { ctaBtn.addEventListener('click', syncBundleSelection); }
    }

    // ---- add-to-cart guard (fail closed) ----
    // A pack whose posted selections differ from what the page shows must never reach the cart
    // (the 3PL would ship the wrong pairs). Only applies to products the app knows as bundles —
    // window.SimpleBundles.productVariants[<variant id>] is inline app-embed data, present before
    // any app JS runs — so any other product submits exactly as before.
    var errWrapEl = root.querySelector('.product-form__error-message-wrapper');
    var errMsgEl = errWrapEl ? errWrapEl.querySelector('.product-form__error-message') : null;
    var guardShown = false;

    function isAppBundle() {
      var idInput = els.form ? els.form.querySelector('input[name="id"]') : null;
      var sb = window.SimpleBundles;
      return !!(idInput && sb && sb.productVariants && sb.productVariants[idInput.value]);
    }

    // null when the form will post exactly the visible picks; otherwise a short reason.
    function payloadMismatch() {
      var selects = bundlePairSelects();
      if (selects.length < els.pairSelects.length) { return 'app pair selects missing'; }
      var fd = new FormData(els.form);
      var bsParts = (fd.get('properties[_bundle_selection]') || '').split(' <> ');
      var reason = null;
      Array.prototype.forEach.call(els.pairSelects, function (sel, idx) {
        if (reason) { return; }
        var pv = pairValue(sel);
        var opt = matchOption(selects[idx], pv.colour, pv.size);
        if (!opt || opt.disabled) { reason = 'pair ' + (idx + 1) + ' has no available app option'; return; }
        var posted = fd.getAll(selects[idx].getAttribute('name'));
        var stale = !posted.length || posted.some(function (v) { return v !== opt.value; });
        if (stale || bsParts[idx] !== opt.value) { reason = 'pair ' + (idx + 1) + ' payload differs from the page'; }
      });
      return reason;
    }

    function guardSubmit(e) {
      syncBundleSelection();
      if (!isAppBundle()) { return; }
      var reason = payloadMismatch();
      if (!reason) {
        if (guardShown && errWrapEl) { errWrapEl.hidden = true; guardShown = false; }
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      if (errWrapEl && errMsgEl) {
        errMsgEl.textContent = cfg.syncErrorLabel || "We couldn't confirm your pack selection. Please refresh the page and try again.";
        errWrapEl.hidden = false;
        guardShown = true;
      }
      if (window.console) { console.error('[bound-bundle-builder] add to cart blocked: ' + reason); }
    }
  }

  // Gallery: thumbnail click swaps the main image (src + srcset + alt).
  function initGallery(g) {
    var main = g.querySelector('[data-bb-gmain]');
    if (!main) { return; }

    // Warm the full-size images into the browser cache on idle so a thumb click swaps
    // instantly instead of waiting on a network fetch. sizes/srcset match the main image
    // so the browser downloads exactly the candidate it will pick at swap time.
    function warmFullImages() {
      var sizes = main.getAttribute('sizes') || '';
      Array.prototype.forEach.call(g.querySelectorAll('[data-bb-thumb]'), function (t) {
        var pre = new Image();
        if (sizes) { pre.sizes = sizes; }
        pre.srcset = t.getAttribute('data-srcset') || '';
        pre.src = t.getAttribute('data-src');
      });
    }
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmFullImages, { timeout: 3000 });
    } else {
      setTimeout(warmFullImages, 800);
    }

    g.addEventListener('click', function (e) {
      var b = e.target.closest('[data-bb-thumb]');
      if (!b) { return; }
      main.src = b.getAttribute('data-src');
      main.srcset = b.getAttribute('data-srcset') || '';
      main.alt = b.getAttribute('data-alt') || '';
      Array.prototype.forEach.call(g.querySelectorAll('[data-bb-thumb]'), function (t) {
        t.classList.toggle('is-on', t === b);
        if (t === b) { t.setAttribute('aria-current', 'true'); } else { t.removeAttribute('aria-current'); }
      });
    });
  }

  // Mobile gallery (≤749px): native scroll-snap track; the "1 / N" counter and the dots follow the
  // swipe, and a dot tap scrolls to its slide.
  function initMobileGallery(mg) {
    var track = mg.querySelector('[data-bb-mtrack]');
    var cur = mg.querySelector('[data-bb-mcur]');
    var dots = mg.querySelectorAll('[data-bb-mdot]');
    if (!track) { return; }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var last = -1;
    function sync() {
      var w = track.clientWidth || 1;
      var i = Math.round(track.scrollLeft / w);
      if (i === last) { return; }
      last = i;
      if (cur) { cur.textContent = String(i + 1); }
      Array.prototype.forEach.call(dots, function (d, k) {
        d.classList.toggle('is-active', k === i);
        if (k === i) { d.setAttribute('aria-current', 'true'); } else { d.removeAttribute('aria-current'); }
      });
    }
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) { return; }
      raf = requestAnimationFrame(function () { raf = 0; sync(); });
    }, { passive: true });
    Array.prototype.forEach.call(dots, function (d) {
      d.addEventListener('click', function () {
        var k = parseInt(d.getAttribute('data-bb-mdot'), 10) || 0;
        track.scrollTo({ left: k * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
      });
    });
  }

  // Deep link to an accordion: `#bound-acc-<heading>` (ids rendered by the buy-box sections, e.g.
  // the gift-box page links to the Boxer's Sizing accordion as `#bound-acc-sizing`). Opens the
  // matching <details> inside .bound-bb__acc and scrolls to it once layout has settled. Runs on
  // boot and on hashchange; does nothing for any other hash.
  function openAccordionFromHash() {
    var hash = window.location.hash || '';
    if (hash.indexOf('#bound-acc-') !== 0) { return; }
    var target = document.getElementById(hash.slice(1));
    if (!target || target.tagName !== 'DETAILS' || !target.closest('.bound-bb__acc')) { return; }
    target.open = true;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(function () {
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      var sum = target.querySelector('summary');
      if (sum) { sum.focus({ preventScroll: true }); }
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-bound-bb]'), initBuilder);
    Array.prototype.forEach.call(document.querySelectorAll('[data-bound-gallery]'), initGallery);
    Array.prototype.forEach.call(document.querySelectorAll('[data-bb-mgallery]'), initMobileGallery);
    openAccordionFromHash();
    window.addEventListener('hashchange', openAccordionFromHash);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
